/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { OrderTemplate } from '@msdyn365-commerce-modules/retail-actions';
import { AuditableOperation, BusinessPartnerUser } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

export interface IAuditMessageResources {
    orderTemplateCreatedText: string;
    orderTemplateSharedText: string;
    orderTemplateModifiedText: string;
    orderTemplateYouText: string;
    orderTemplateForText: string;
    orderTemplateByText?: string;
}

/**
 * Calculates the {operator} text from an AuditableOperation and the current user.
 *
 * Logic follows two persona paths:
 *  - Account manager (current user has StaffId)
 *  - B2B buyer     (current user has only ContactPersonId)
 *
 * @param operation - The audit operation (CreatedBy / UpdatedBy / SharedBy).
 * @param businessPartnerUser - The current logged-in user.
 * @param resources - Localised "You" and "for" tokens.
 */
export const getOperatorText = (
    operation: AuditableOperation | undefined,
    businessPartnerUser: BusinessPartnerUser | undefined,
    resources: Pick<IAuditMessageResources, 'orderTemplateYouText' | 'orderTemplateForText'>
): string => {
    if (!operation || !businessPartnerUser) {
        return '';
    }

    const { orderTemplateYouText, orderTemplateForText } = resources;
    const currentStaffId = businessPartnerUser.StaffId;
    const currentContactPersonId = businessPartnerUser.ContactPersonId;

    // Case 1: Current user is an account manager (has StaffId)
    if (currentStaffId) {
        if (operation.StaffId && currentStaffId === operation.StaffId) {
            // "You for {ContactPersonFirstName}"
            return operation.ContactPersonFirstName
                ? `${orderTemplateYouText} ${orderTemplateForText} ${operation.ContactPersonFirstName}`
                : orderTemplateYouText;
        }

        if (!operation.StaffId) {
            // "{ContactPersonFirstName}"
            return operation.ContactPersonFirstName || '';
        }

        // "{EmployeeName} for {ContactPersonFirstName}"
        return operation.ContactPersonFirstName
            ? `${operation.StaffName || ''} ${orderTemplateForText} ${operation.ContactPersonFirstName}`
            : operation.StaffName || '';
    }

    // Case 2: Current user is a B2B buyer
    if (currentContactPersonId && currentContactPersonId === operation.ContactPersonId) {
        if (!operation.StaffId) {
            // "you"
            return orderTemplateYouText;
        }

        // "{EmployeeName} for you"
        return `${operation.StaffName || ''} ${orderTemplateForText} ${orderTemplateYouText}`;
    }

    // Fallback: "{ContactPersonFirstName}"
    return operation.ContactPersonFirstName || '';
};

export const getSharedMessageComponent = (
    orderTemplate: OrderTemplate,
    businessPartnerUser: BusinessPartnerUser | undefined,
    resources: IAuditMessageResources,
    formatOperationDate?: (d: Date) => string
): React.ReactNode | null => {
    if (!orderTemplate) {
        return null;
    }

    const { productList } = orderTemplate;

    if (!productList?.IsShared) {
        return null;
    }

    const sharedOperation = productList.SharedBy;
    const sharedDate = sharedOperation?.OperationDateTime;
    const formattedDate = sharedDate && formatOperationDate ? ` ${formatOperationDate(sharedDate)}` : '';
    const byText = resources.orderTemplateByText ? ` ${resources.orderTemplateByText}` : '';
    const actionText = `${resources.orderTemplateSharedText}${formattedDate}${byText}`;
    const operatorText = getOperatorText(sharedOperation, businessPartnerUser, resources);

    return (
        <div className='organization-order-template shared'>
            <span className='creator'>
                {actionText}
                {operatorText && <span>{` ${operatorText}`}</span>}
            </span>
        </div>
    );
};

export const getCreatedOrModifiedMessageComponent = (
    orderTemplate: OrderTemplate,
    businessPartnerUser: BusinessPartnerUser | undefined,
    resources: IAuditMessageResources,
    formatOperationDate?: (d: Date) => string
): React.ReactNode | null => {
    if (!orderTemplate) {
        return null;
    }

    const { productList } = orderTemplate;
    const createdDate = productList?.CreatedBy?.OperationDateTime;
    const updatedDate = productList?.UpdatedBy?.OperationDateTime;
    const isModified = createdDate && updatedDate && updatedDate > createdDate;

    const operation = isModified ? productList?.UpdatedBy : productList?.CreatedBy;
    const actionLabel = isModified ? resources.orderTemplateModifiedText : resources.orderTemplateCreatedText;
    const operationDate = isModified ? updatedDate : createdDate;
    const formattedDate = operationDate && formatOperationDate ? ` ${formatOperationDate(operationDate)}` : '';
    const byText = resources.orderTemplateByText ? ` ${resources.orderTemplateByText}` : '';
    const actionText = `${actionLabel}${formattedDate}${byText}`;
    const operatorText = getOperatorText(operation, businessPartnerUser, resources);

    return (
        <div className={`organization-order-template ${isModified ? 'modified' : 'created'}`}>
            <span className='creator'>
                {actionText}
                {operatorText && <span>{` ${operatorText}`}</span>}
            </span>
        </div>
    );
};
